import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiCopy, FiCheck, FiSearch, FiArrowUpRight } from "react-icons/fi";
import { Helmet } from "react-helmet";
import axios from "axios";
import { REACT_APP_API_PORT } from "../Api";

/*
  Flow AI Secret Codes Library
  - 205 Flow AI camera and video prompt-style codes
  - Search + one-click copy
  - Reference-inspired editorial design
  - Two extra power-user sections
  - Important: not every slash shortcut below is a native Google Flow command; treat these as prompt-style camera cues.
*/

const FREE_COMMAND_LIMIT = 30;
const UNLOCK_CACHE_KEY = "flow_secret_code_user";

const LEARN_URL = "https://learn.nextfutureai.in/web/checkout/6926a3a90b33bbee0d20d989?couponcode=FIRST50&purchaseNow=true";

const COMMANDS = [
  { id: 1, number: 1, command: "/zoom", title: "ZOOM", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 2, number: 2, command: "/zoomin", title: "ZOOMIN", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 3, number: 3, command: "/zoomout", title: "ZOOMOUT", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 4, number: 4, command: "/slowzoom", title: "SLOWZOOM", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 5, number: 5, command: "/slowzoomin", title: "SLOWZOOMIN", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 6, number: 6, command: "/slowzoomout", title: "SLOWZOOMOUT", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 7, number: 7, command: "/fastzoom", title: "FASTZOOM", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 8, number: 8, command: "/dramaticzoom", title: "DRAMATICZOOM", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 9, number: 9, command: "/smoothzoom", title: "SMOOTHZOOM", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 10, number: 10, command: "/dolly", title: "DOLLY", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 11, number: 11, command: "/dollyin", title: "DOLLYIN", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 12, number: 12, command: "/dollyout", title: "DOLLYOUT", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 13, number: 13, command: "/slowdollyin", title: "SLOWDOLLYIN", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 14, number: 14, command: "/slowdollyout", title: "SLOWDOLLYOUT", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 15, number: 15, command: "/dollyzoom", title: "DOLLYZOOM", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 16, number: 16, command: "/pushin", title: "PUSHIN", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 17, number: 17, command: "/pullout", title: "PULLOUT", description: "Control camera push-ins, pull-outs and zoom speed for entrances, portraits, product reveals and cinematic scenes." },
  { id: 18, number: 18, command: "/orbit", title: "ORBIT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 19, number: 19, command: "/orbitleft", title: "ORBITLEFT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 20, number: 20, command: "/orbitright", title: "ORBITRIGHT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 21, number: 21, command: "/orbit180", title: "ORBIT180", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 22, number: 22, command: "/orbit360", title: "ORBIT360", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 23, number: 23, command: "/sloworbit", title: "SLOWORBIT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 24, number: 24, command: "/fastorbit", title: "FASTORBIT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 25, number: 25, command: "/arcshot", title: "ARCSHOT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 26, number: 26, command: "/spin", title: "SPIN", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 27, number: 27, command: "/rotation", title: "ROTATION", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 28, number: 28, command: "/rotateleft", title: "ROTATELEFT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 29, number: 29, command: "/rotateright", title: "ROTATERIGHT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 30, number: 30, command: "/360shot", title: "360SHOT", description: "Create circular camera movement around a subject, from subtle arcs to full 360-degree rotations." },
  { id: 31, number: 31, command: "/pan", title: "PAN", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 32, number: 32, command: "/panleft", title: "PANLEFT", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 33, number: 33, command: "/panright", title: "PANRIGHT", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 34, number: 34, command: "/slowpan", title: "SLOWPAN", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 35, number: 35, command: "/fastpan", title: "FASTPAN", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 36, number: 36, command: "/tilt", title: "TILT", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 37, number: 37, command: "/tiltup", title: "TILTUP", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 38, number: 38, command: "/tiltdown", title: "TILTDOWN", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 39, number: 39, command: "/slowtilt", title: "SLOWTILT", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 40, number: 40, command: "/whip", title: "WHIP", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 41, number: 41, command: "/whippan", title: "WHIPPAN", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 42, number: 42, command: "/whip-pan", title: "WHIP PAN", description: "Describe horizontal pans and vertical tilts for reveals, landscapes, buildings and character framing." },
  { id: 43, number: 43, command: "/truck", title: "TRUCK", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 44, number: 44, command: "/truckleft", title: "TRUCKLEFT", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 45, number: 45, command: "/truckright", title: "TRUCKRIGHT", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 46, number: 46, command: "/pedestal", title: "PEDESTAL", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 47, number: 47, command: "/pedestalup", title: "PEDESTALUP", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 48, number: 48, command: "/pedestaldown", title: "PEDESTALDOWN", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 49, number: 49, command: "/crane", title: "CRANE", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 50, number: 50, command: "/craneup", title: "CRANEUP", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 51, number: 51, command: "/cranedown", title: "CRANEDOWN", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 52, number: 52, command: "/craneshot", title: "CRANESHOT", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 53, number: 53, command: "/sweepingcrane", title: "SWEEPINGCRANE", description: "Move the camera sideways, vertically or upward for larger cinematic reveals and scale." },
  { id: 54, number: 54, command: "/drone", title: "DRONE", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 55, number: 55, command: "/dronefly", title: "DRONEFLY", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 56, number: 56, command: "/droneforward", title: "DRONEFORWARD", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 57, number: 57, command: "/dronebackward", title: "DRONEBACKWARD", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 58, number: 58, command: "/droneup", title: "DRONEUP", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 59, number: 59, command: "/dronedown", title: "DRONEDOWN", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 60, number: 60, command: "/droneorbit", title: "DRONEORBIT", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 61, number: 61, command: "/droneorbit360", title: "DRONEORBIT360", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 62, number: 62, command: "/aerial", title: "AERIAL", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 63, number: 63, command: "/aerialshot", title: "AERIALSHOT", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 64, number: 64, command: "/aerialview", title: "AERIALVIEW", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 65, number: 65, command: "/aerialreveal", title: "AERIALREVEAL", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 66, number: 66, command: "/topdown", title: "TOPDOWN", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 67, number: 67, command: "/birdseye", title: "BIRDSEYE", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 68, number: 68, command: "/flyover", title: "FLYOVER", description: "Create aerial, flyover and top-down camera directions for travel, landscapes, architecture and establishing shots." },
  { id: 69, number: 69, command: "/fpv", title: "FPV", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 70, number: 70, command: "/fpvdrone", title: "FPVDRONE", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 71, number: 71, command: "/fpvflythrough", title: "FPVFLYTHROUGH", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 72, number: 72, command: "/flythrough", title: "FLYTHROUGH", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 73, number: 73, command: "/tracking", title: "TRACKING", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 74, number: 74, command: "/trackingshot", title: "TRACKINGSHOT", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 75, number: 75, command: "/follow", title: "FOLLOW", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 76, number: 76, command: "/followshot", title: "FOLLOWSHOT", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 77, number: 77, command: "/followcam", title: "FOLLOWCAM", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 78, number: 78, command: "/sidetracking", title: "SIDETRACKING", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 79, number: 79, command: "/forwardtracking", title: "FORWARDTRACKING", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 80, number: 80, command: "/backwardtracking", title: "BACKWARDTRACKING", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 81, number: 81, command: "/steadicam", title: "STEADICAM", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 82, number: 82, command: "/gimbal", title: "GIMBAL", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 83, number: 83, command: "/smoothfollow", title: "SMOOTHFOLLOW", description: "Keep the camera moving with the subject for walking sequences, fly-throughs and energetic follow shots." },
  { id: 84, number: 84, command: "/handheld", title: "HANDHELD", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 85, number: 85, command: "/shakycam", title: "SHAKYCAM", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 86, number: 86, command: "/static", title: "STATIC", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 87, number: 87, command: "/fixedcamera", title: "FIXEDCAMERA", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 88, number: 88, command: "/smoothcamera", title: "SMOOTHCAMERA", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 89, number: 89, command: "/cinematiccamera", title: "CINEMATICCAMERA", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 90, number: 90, command: "/pov", title: "POV", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 91, number: 91, command: "/firstperson", title: "FIRSTPERSON", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 92, number: 92, command: "/thirdperson", title: "THIRDPERSON", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 93, number: 93, command: "/overtheshoulder", title: "OVERTHESHOULDER", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 94, number: 94, command: "/lowangle", title: "LOWANGLE", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 95, number: 95, command: "/highangle", title: "HIGHANGLE", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 96, number: 96, command: "/eyelevel", title: "EYELEVEL", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 97, number: 97, command: "/groundlevel", title: "GROUNDLEVEL", description: "Set the camera feel and viewpoint, from handheld and POV to static, low-angle and eye-level shots." },
  { id: 98, number: 98, command: "/closeup", title: "CLOSEUP", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 99, number: 99, command: "/extremecloseup", title: "EXTREMECLOSEUP", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 100, number: 100, command: "/mediumcloseup", title: "MEDIUMCLOSEUP", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 101, number: 101, command: "/mediumshot", title: "MEDIUMSHOT", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 102, number: 102, command: "/wideshot", title: "WIDESHOT", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 103, number: 103, command: "/wide", title: "WIDE", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 104, number: 104, command: "/extremewide", title: "EXTREMEWIDE", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 105, number: 105, command: "/fullbody", title: "FULLBODY", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 106, number: 106, command: "/establishingshot", title: "ESTABLISHINGSHOT", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 107, number: 107, command: "/heroshot", title: "HEROSHOT", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 108, number: 108, command: "/macro", title: "MACRO", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 109, number: 109, command: "/symmetrical", title: "SYMMETRICAL", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 110, number: 110, command: "/centerframe", title: "CENTERFRAME", description: "Choose framing and composition, from close-ups and portraits to wide establishing and full-body shots." },
  { id: 111, number: 111, command: "/rackfocus", title: "RACKFOCUS", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 112, number: 112, command: "/focuspull", title: "FOCUSPULL", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 113, number: 113, command: "/shallowfocus", title: "SHALLOWFOCUS", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 114, number: 114, command: "/deepfocus", title: "DEEPFOCUS", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 115, number: 115, command: "/softfocus", title: "SOFTFOCUS", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 116, number: 116, command: "/foregroundblur", title: "FOREGROUNDBLUR", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 117, number: 117, command: "/backgroundblur", title: "BACKGROUNDBLUR", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 118, number: 118, command: "/bokeh", title: "BOKEH", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 119, number: 119, command: "/shallowdepth", title: "SHALLOWDEPTH", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 120, number: 120, command: "/wideangle", title: "WIDEANGLE", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 121, number: 121, command: "/telephoto", title: "TELEPHOTO", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 122, number: 122, command: "/macrofocus", title: "MACROFOCUS", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 123, number: 123, command: "/lensflare", title: "LENSFLARE", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 124, number: 124, command: "/anamorphic", title: "ANAMORPHIC", description: "Control focus behavior, depth of field and lens character for cinematic separation and visual emphasis." },
  { id: 125, number: 125, command: "/slowmo", title: "SLOWMO", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 126, number: 126, command: "/slowmotion", title: "SLOWMOTION", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 127, number: 127, command: "/timelapse", title: "TIMELAPSE", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 128, number: 128, command: "/hyperlapse", title: "HYPERLAPSE", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 129, number: 129, command: "/speedramp", title: "SPEEDRAMP", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 130, number: 130, command: "/speedup", title: "SPEEDUP", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 131, number: 131, command: "/motionblur", title: "MOTIONBLUR", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 132, number: 132, command: "/smoothmotion", title: "SMOOTHMOTION", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 133, number: 133, command: "/naturalmotion", title: "NATURALMOTION", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 134, number: 134, command: "/freeze", title: "FREEZE", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 135, number: 135, command: "/freezeframe", title: "FREEZEFRAME", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 136, number: 136, command: "/bullet-time", title: "BULLET TIME", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 137, number: 137, command: "/bullettime", title: "BULLETTIME", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 138, number: 138, command: "/reverse", title: "REVERSE", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 139, number: 139, command: "/loop", title: "LOOP", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 140, number: 140, command: "/seamlessloop", title: "SEAMLESSLOOP", description: "Shape pacing with slow motion, time-lapse, speed ramps, motion blur, freezes and seamless loops." },
  { id: 141, number: 141, command: "/transition", title: "TRANSITION", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 142, number: 142, command: "/smoothtransition", title: "SMOOTHTRANSITION", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 143, number: 143, command: "/seamlesstransition", title: "SEAMLESSTRANSITION", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 144, number: 144, command: "/morph", title: "MORPH", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 145, number: 145, command: "/morphtransition", title: "MORPHTRANSITION", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 146, number: 146, command: "/glitch", title: "GLITCH", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 147, number: 147, command: "/glitchtransition", title: "GLITCHTRANSITION", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 148, number: 148, command: "/fadein", title: "FADEIN", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 149, number: 149, command: "/fadeout", title: "FADEOUT", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 150, number: 150, command: "/matchcut", title: "MATCHCUT", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 151, number: 151, command: "/flashtransition", title: "FLASHTRANSITION", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 152, number: 152, command: "/foregroundwipe", title: "FOREGROUNDWIPE", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 153, number: 153, command: "/smokereveal", title: "SMOKEREVEAL", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 154, number: 154, command: "/objectreveal", title: "OBJECTREVEAL", description: "Describe visual transitions between shots or states, including morphs, wipes, fades, glitches and reveals." },
  { id: 155, number: 155, command: "/cinematic", title: "CINEMATIC", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 156, number: 156, command: "/epicshot", title: "EPICSHOT", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 157, number: 157, command: "/epicreveal", title: "EPICREVEAL", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 158, number: 158, command: "/dramatic", title: "DRAMATIC", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 159, number: 159, command: "/filmlook", title: "FILMLOOK", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 160, number: 160, command: "/filmic", title: "FILMIC", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 161, number: 161, command: "/filmgrain", title: "FILMGRAIN", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 162, number: 162, command: "/anamorphiclook", title: "ANAMORPHICLOOK", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 163, number: 163, command: "/hdr", title: "HDR", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 164, number: 164, command: "/realistic", title: "REALISTIC", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 165, number: 165, command: "/photorealistic", title: "PHOTOREALISTIC", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 166, number: 166, command: "/ultrarealistic", title: "ULTRAREALISTIC", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 167, number: 167, command: "/dramaticlighting", title: "DRAMATICLIGHTING", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 168, number: 168, command: "/volumetriclight", title: "VOLUMETRICLIGHT", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 169, number: 169, command: "/goldenhour", title: "GOLDENHOUR", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 170, number: 170, command: "/bluehour", title: "BLUEHOUR", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 171, number: 171, command: "/backlight", title: "BACKLIGHT", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 172, number: 172, command: "/rimlight", title: "RIMLIGHT", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 173, number: 173, command: "/silhouette", title: "SILHOUETTE", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 174, number: 174, command: "/neonlight", title: "NEONLIGHT", description: "Add film-style mood, lighting and visual character such as golden hour, anamorphic looks and dramatic light." },
  { id: 175, number: 175, command: "/fog", title: "FOG", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 176, number: 176, command: "/mist", title: "MIST", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 177, number: 177, command: "/smoke", title: "SMOKE", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 178, number: 178, command: "/rain", title: "RAIN", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 179, number: 179, command: "/heavyrain", title: "HEAVYRAIN", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 180, number: 180, command: "/snow", title: "SNOW", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 181, number: 181, command: "/wind", title: "WIND", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 182, number: 182, command: "/dust", title: "DUST", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 183, number: 183, command: "/particles", title: "PARTICLES", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 184, number: 184, command: "/clouds", title: "CLOUDS", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 185, number: 185, command: "/storm", title: "STORM", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 186, number: 186, command: "/sunrays", title: "SUNRAYS", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 187, number: 187, command: "/godrays", title: "GODRAYS", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 188, number: 188, command: "/floatingparticles", title: "FLOATINGPARTICLES", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 189, number: 189, command: "/fallingleaves", title: "FALLINGLEAVES", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 190, number: 190, command: "/flowerpetals", title: "FLOWERPETALS", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 191, number: 191, command: "/watersplash", title: "WATERSPLASH", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 192, number: 192, command: "/reflection", title: "REFLECTION", description: "Add moving environmental details such as fog, rain, snow, particles, clouds, wind and reflections." },
  { id: 193, number: 193, command: "/reveal", title: "REVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 194, number: 194, command: "/slowreveal", title: "SLOWREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 195, number: 195, command: "/characterreveal", title: "CHARACTERREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 196, number: 196, command: "/facereveal", title: "FACEREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 197, number: 197, command: "/productreveal", title: "PRODUCTREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 198, number: 198, command: "/environmentreveal", title: "ENVIRONMENTREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 199, number: 199, command: "/locationreveal", title: "LOCATIONREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 200, number: 200, command: "/aerialreveal", title: "AERIALREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 201, number: 201, command: "/pullbackreveal", title: "PULLBACKREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 202, number: 202, command: "/tiltupreveal", title: "TILTUPREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 203, number: 203, command: "/doorreveal", title: "DOORREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 204, number: 204, command: "/smokereveal", title: "SMOKEREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
  { id: 205, number: 205, command: "/silhouettereveal", title: "SILHOUETTEREVEAL", description: "Reveal characters, products, faces and locations through camera movement, silhouettes and environmental transitions." },
];

const CATEGORY_GROUPS = [
  { name: "Zoom & Dolly", range: [0, 17] },
  { name: "Orbit & Rotation", range: [17, 30] },
  { name: "Pan & Tilt", range: [30, 42] },
  { name: "Truck, Pedestal & Crane", range: [42, 53] },
  { name: "Drone & Aerial", range: [53, 68] },
  { name: "FPV & Tracking", range: [68, 83] },
  { name: "POV & Camera Style", range: [83, 97] },
  { name: "Camera Shots", range: [97, 110] },
  { name: "Focus & Lens", range: [110, 124] },
  { name: "Speed & Motion", range: [124, 140] },
  { name: "Transitions", range: [140, 154] },
  { name: "Cinematic Effects", range: [154, 174] },
  { name: "Atmosphere & Environment", range: [174, 192] },
  { name: "Reveal Codes", range: [192, 205] },
];

const comboSections = [
  [
    "Best Google Flow Code Combinations",
    [
      ["Cinematic Hero Entry", "/slowmo /dollyin /lowangle /heroshot /cinematic", "Slow-motion hero entrance with a low-angle cinematic push-in."],
      ["Viral Walking Video", "/backwardtracking /steadicam /mediumshot /slowmo /goldenhour", "Smooth tracking shot for a polished walking sequence."],
      ["360° Character Video", "/orbit360 /fullbody /smoothcamera /cinematic", "A full-body character shot with a smooth circular camera move."],
      ["Drone Travel Video", "/dronefly /aerialreveal /wide /goldenhour /cinematic", "Aerial travel reveal that opens up the full landscape."],
      ["Emotional Portrait", "/closeup /dollyin /rackfocus /shallowdepth /softfocus", "Pull attention from the environment into the subject's expression."],
      ["Epic Landscape Reveal", "/craneup /extremewide /volumetriclight /epicreveal", "Start closer and rise into a large-scale landscape reveal."],
      ["Product Advertisement", "/orbit /closeup /macrofocus /dramaticlighting /cinematic", "Premium product movement with close detail and dramatic lighting."],
      ["First-Person Scene", "/pov /firstperson /handheld /naturalmotion", "Immersive first-person movement for exploration scenes."],
      ["Cinematic Rain Scene", "/slowmo /tracking /rain /backlight /cinematic", "Slow, atmospheric tracking with rain and backlight."],
      ["Professional Film Look", "/dollyin /anamorphic /filmgrain /shallowdepth /cinematic", "A film-style push-in with shallow depth and cinematic texture."]
    ]
  ],
  [
    "How to Use Google Flow Secret Codes Correctly",
    [
      ["STEP 1 — Choose your subject", "Start with the subject and action first. Example: A young man walking through a city street."],
      ["STEP 2 — Choose one main camera movement", "Pick one primary movement such as /backwardtracking instead of stacking many competing movements."],
      ["STEP 3 — Choose your shot", "Add framing such as /mediumshot, /closeup or /wide depending on what should dominate the scene."],
      ["STEP 4 — Choose lighting", "Add a lighting direction such as /goldenhour, /backlight or /dramaticlighting."],
      ["STEP 5 — Add style", "Finish with a supporting style cue such as /cinematic, then expand the shortcuts into natural language."],
      ["PRO TIP — Avoid conflicting camera directions", "Using /drone, /macro, /pov, /orbit and /dolly together can create competing instructions. Keep the main movement clear."],
      ["PRO TIP — Describe both visual states for transitions", "For a transition, explain the starting visual and ending visual instead of relying on /transition alone."],
      ["PRO TIP — Expand shortcuts into natural language", "A short code combination works best as a planning cue; turn it into a complete visual description for more usable results."]
    ]
  ]
];
export default function GoogleFlowGuide() {
  const [search, setSearch] = useState("");
  const [activeGroup, setActiveGroup] = useState("All");
  const [copied, setCopied] = useState("");

  const [isUnlocked, setIsUnlocked] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", email: "", mobile: "" });
  const [leadError, setLeadError] = useState("");
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [isSavingMobile, setIsSavingMobile] = useState(false);
  const unlockTriggerRef = useRef(null);

  useEffect(() => {
    try {
      const saved =
        localStorage.getItem(UNLOCK_CACHE_KEY) ||
        sessionStorage.getItem(UNLOCK_CACHE_KEY);

      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.name && parsed?.email && parsed?.mobile) {
          setLeadForm(parsed);
          setIsUnlocked(true);
        }
      }
    } catch {}
  }, []);

  useEffect(() => {
    if (isUnlocked || search.trim() || activeGroup !== "All") return;

    try {
      if (sessionStorage.getItem("flow_secret_code_popup_shown") === "1") return;
    } catch {}

    const target = unlockTriggerRef.current;
    if (!target || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0]?.isIntersecting) return;

        setShowUnlockModal(true);

        try {
          sessionStorage.setItem("flow_secret_code_popup_shown", "1");
        } catch {}

        observer.disconnect();
      },
      { root: null, rootMargin: "0px 0px 120px 0px", threshold: 0.01 }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isUnlocked, search, activeGroup]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return COMMANDS.filter((item, index) => {
      const group = CATEGORY_GROUPS.find(g => index >= g.range[0] && index < g.range[1])?.name;
      const groupMatch = activeGroup === "All" || group === activeGroup;
      const searchMatch = !q || `${item.command} ${item.title} ${item.description} ${group}`.toLowerCase().includes(q);
      return groupMatch && searchMatch;
    });
  }, [search, activeGroup]);

  const copy = async (item) => {
    if (!isUnlocked && item.number > FREE_COMMAND_LIMIT) {
      setShowUnlockModal(true);
      return;
    }

    const text = `${item.command}\n${item.description}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(item.command);
      setTimeout(() => setCopied(""), 1400);
    } catch {}
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    const name = leadForm.name.trim();
    const email = leadForm.email.trim().toLowerCase();
    const mobile = leadForm.mobile.trim();

    if (!name) {
      setLeadError("Please enter your name");
      return;
    }

    if (!email) {
      setLeadError("Please enter your email");
      return;
    }

    if (!mobile) {
      setLeadError("Please enter your mobile number");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLeadError("Please enter a valid email.");
      return;
    }

    if (!/^[6-9]\d{9}$/.test(mobile)) {
    setLeadError("Please enter a valid 10-digit mobile number.");
    return;
  }

    try {
      setIsSavingMobile(true);
      setLeadError("");

      const res = await axios.post(
      `${REACT_APP_API_PORT}user/homepage/guide_users_googleflow`,
      {
        name,
        email,
        mobile,
      }
    );

      if (!res.data?.status) {
        setLeadError(
          res.data?.message || "Something went wrong. Please try again."
        );
        return;
      }

      const userDetails = { name, email, mobile };

      try {
        localStorage.setItem(
          UNLOCK_CACHE_KEY,
          JSON.stringify(userDetails)
        );
        sessionStorage.setItem(
          UNLOCK_CACHE_KEY,
          JSON.stringify(userDetails)
        );
      } catch {}

      setLeadForm(userDetails);
      setIsUnlocked(true);
      setShowUnlockModal(false);
      setLeadError("");
    } catch (error) {
      console.error("Unlock API Error:", error);
      setLeadError(
        error?.response?.data?.message ||
          "Something went wrong. Please try again."
      );
    } finally {
      setIsSavingMobile(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#000000] text-[#FFFFFF]">
      <Helmet>
        <title>200+ Google Flow Secret Codes 2026 | Flow AI Guide</title>
        <meta name="description" content="Explore 205+ Flow AI secret codes for camera movement, shots, focus, motion, transitions, cinematic effects and reveals." />
      </Helmet>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,600;0,700;1,600;1,700&display=swap');
        .cmd-serif { font-family:'Playfair Display', Georgia, serif; }
        .cmd-sans { font-family:'DM Sans', Arial, sans-serif; }
        html, body, #root { min-height:100%; margin:0; }
        body { background:#000000; color:#FFFFFF; }
        .claude-bg { min-height:100vh; overflow-x:hidden; overflow-y:visible; background:linear-gradient(180deg,#000000 0%,#000000 100%); }
        .shortcut-card { box-shadow:0 10px 28px rgba(0,0,0,.35); }
        .shortcut-card:hover { box-shadow:0 16px 34px rgba(0,0,0,.55); transform:translateY(-2px); }
        .learn-strip:hover { border-color:#111111; background:rgba(255,255,255,.08); }
        .scrollbar-hide { scrollbar-width:none; -ms-overflow-style:none; }
        .scrollbar-hide::-webkit-scrollbar { display:none; }
      `}</style>

      <main className="cmd-sans claude-bg min-h-screen">
        <section className="mx-auto max-w-[1260px] px-5 pb-10 pt-9 sm:px-8 lg:px-10">
          <div className="mx-auto max-w-[980px] text-center">
            <div className="inline-flex items-center rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-5 py-2 text-[10px] font-bold uppercase tracking-[.18em] text-white shadow-[0_8px_22px_rgba(255,255,255,.10)]">
              ✦ Google Flow Secret Code Library
            </div>
            <h1 className="cmd-serif mt-6 text-[40px] font-bold leading-[1.05] tracking-[-.035em] text-[#FFFFFF] sm:text-[58px]">
              200+ Google Flow <span className="block italic text-[#FFFFFF]">Secret Codes 2026</span>
            </h1>
            <div className="mx-auto mt-4 h-[3px] w-14 rounded-full bg-[#111111]" />
            <p className="mx-auto mt-6 max-w-[790px] text-[14px] leading-6 text-[#AAAAAA] sm:text-[16px]">
              A visual secret-code library for camera movement, cinematic shots, motion, transitions, atmosphere and AI video workflows.
            </p>
            <div className="mt-5 text-[11px] leading-5 text-[#999999]">
              These are <strong>prompt-style camera cues</strong>; use them as shorthand and expand them into natural-language instructions when needed.
            </div>
            <div className="mt-7 flex flex-wrap justify-center gap-3 text-[10px] font-bold uppercase tracking-[.12em] text-[#111]">
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">⚡ 205+ Secret Codes</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">🎥 Camera + Motion</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">🎬 Cinematic + Style</span>
              <span className="rounded-full border border-[#333] bg-white px-4 py-2">📋 One-Click Copy</span>
            </div>
          </div>

          <div className="mx-auto mt-10 max-w-[1040px] rounded-[26px] border border-[#2A2A2A] bg-[#0A0A0A]/80 p-5 shadow-[0_18px_55px_rgba(0,0,0,.45)] sm:p-7">
            <h2 className="cmd-serif text-[24px] font-bold text-[#FFFFFF]">🎯 How to use these secret codes</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-3">
              {[
                ["STEP 1", "Pick a code", "Choose the camera movement, shot, style or effect that matches your scene."],
                ["STEP 2", "Add your scene", "Describe the subject, action, environment and visual intent."],
                ["STEP 3", "Copy & use", "Copy the code and combine it with your natural-language prompt."],
              ].map(([a,b,c]) => (
                <div key={a} className="rounded-[18px] border border-[#2A2A2A] bg-white/5 p-5">
                  <div className="text-[10px] font-bold tracking-[.15em] text-[#FFFFFF]">{a}</div>
                  <div className="mt-2 text-[15px] font-bold text-[#EEEEEE]">{b}</div>
                  <p className="mt-1 text-[12px] leading-5 text-[#AAAAAA]">{c}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1260px] px-5 pb-16 sm:px-8 lg:px-10">
          <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <div className="inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em] text-white">ALL 205+ SECRET CODES</div>
              <h2 className="cmd-serif mt-4 text-[34px] font-bold tracking-[-.03em] text-[#FFFFFF] sm:text-[46px]">The Complete Secret Code Library</h2>
              <p className="mt-2 text-sm text-[#AAAAAA]">Search, filter and copy any Flow AI camera code in seconds.</p>
            </div>
            <div className="w-full lg:w-[370px]">
              <div className="relative">
                <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[#999999]" />
                <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search codes, camera moves or uses..." className="h-12 w-full rounded-2xl border border-[#2A2A2A] bg-white/5 pl-11 pr-4 text-sm text-[#FFFFFF] outline-none transition focus:border-[#111111] focus:ring-4 focus:ring-[#111111]/10" />
              </div>
            </div>
          </div>

          <div className="mb-8 flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            <button onClick={()=>setActiveGroup("All")} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition ${activeGroup === "All" ? "bg-[#FFFFFF] text-black" : "border border-[#2A2A2A] bg-white/5 text-[#fff] hover:border-[#555555]"}`}>All · 205+</button>
            {CATEGORY_GROUPS.map(group => (
              <button key={group.name} onClick={()=>setActiveGroup(group.name)} className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition ${activeGroup === group.name ? "bg-[#FFFFFF] text-black" : "border border-[#2A2A2A] bg-white/5 text-[#fff] hover:border-[#555555]"}`}>{group.name}</button>
            ))}
          </div>

          <div className="mb-5 text-sm font-semibold text-[#AAAAAA]">Showing <strong>{filtered.length}</strong> code{filtered.length === 1 ? "" : "s"}</div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
  {filtered.map((item, idx) => (
    <React.Fragment key={item.id}>

      <article className="shortcut-card group relative rounded-[20px] border border-[#E8E8E8] bg-[#FFFFFF] p-5 transition duration-200 hover:-translate-y-0.5">

        {!isUnlocked && item.number > FREE_COMMAND_LIMIT && (
          <div
            className="absolute inset-0 z-20 flex cursor-pointer flex-col items-center justify-center rounded-[20px] bg-[#111111]/90 px-5 text-center backdrop-blur-[3px]"
            onClick={() => setShowUnlockModal(true)}
          >
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-full border border-[#444444] bg-[#222222] text-lg">
              🔒
            </div>
            <p className="text-sm font-bold text-white">
              This Shortcut is Locked
            </p>
            <p className="mt-1 text-[11px] leading-4 text-[#999999]">
              Submit your details to unlock all 205+ secret codes
            </p>
          </div>
        )}

        {/* Pink left accent */}
        <div className="absolute left-0 top-5 h-10 w-[3px] rounded-r-full bg-[#FF5F87]" />

        <div className="flex items-start justify-between gap-3 pl-2">

          <div className="min-w-0 flex-1">

            <div className="flex items-center justify-between gap-2">

              {/* Command */}
              <code className="rounded-lg bg-[#FFF1F4] px-2.5 py-1 text-[15px] font-bold tracking-[-.02em] text-[#111111]">
                {item.command}
              </code>

              {/* Number */}
              <span className="shrink-0 rounded-full bg-[#111111] px-2 py-1 text-[8px] font-bold uppercase tracking-[.05em] text-white">
                #{String(item.id).padStart(3, "0")}
              </span>

            </div>

            {/* Category */}
            <div className="mt-4 text-[10px] font-bold uppercase tracking-[.12em] text-[#111111]">
              {item.title}
            </div>

            {/* Description */}
            <p className="mt-2 text-[13px] leading-5 text-[#6B4A55]">
              {item.description}
            </p>

          </div>

          {/* Copy Button */}
          <button
            onClick={() => copy(item)}
            aria-label={`Copy ${item.command}`}
            className={`shrink-0 rounded-xl p-2.5 transition ${
              copied === item.command
                ? "bg-[#FF5F87] text-white"
                : "bg-[#FFF1F4] text-[#9A6073] hover:bg-[#FF5F87] hover:text-white"
            }`}
          >
            {copied === item.command ? <FiCheck /> : <FiCopy />}
          </button>

        </div>

        {/* Bottom CTA */}
        <div className="mt-4 border-t border-[#E8DDE1] pt-3 text-[10px] font-semibold uppercase tracking-[.10em] text-[#9A7A84]">
          Type{" "}
          <span className="text-[#FF5F87]">
            {item.command}
          </span>{" "}
          + your request

          <a
            href={LEARN_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="float-right font-bold text-[#FF5F87] hover:underline"
          >
            LEARN HOW →
          </a>
        </div>

      </article>

      {item.number === FREE_COMMAND_LIMIT &&
        !isUnlocked &&
        !search.trim() &&
        activeGroup === "All" && (
          <div
            ref={unlockTriggerRef}
            className="col-span-full h-2"
            aria-hidden="true"
          />
        )}

      {/* Learn More Strip */}
      {(idx + 1) % 12 === 0 && idx !== filtered.length - 1 && (
        <a
          href={LEARN_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="learn-strip md:col-span-2 xl:col-span-3 rounded-[18px] border border-[#2A2A2A] bg-[#181818] px-5 py-3.5 transition hover:bg-[#202020]"
        >
          <div className="flex items-center justify-between gap-4">

            <div>
              <div className="text-[9px] font-bold uppercase tracking-[.16em] text-[#FF5F87]">
                Keep creating
              </div>

              <div className="mt-1 text-[12px] font-semibold text-[#CCCCCC]">
                Explore practical AI video workflows and prompting techniques.
              </div>
            </div>

            <span className="shrink-0 text-[10px] font-bold uppercase tracking-[.12em] text-[#FF5F87]">
              Learn more →
            </span>

          </div>
        </a>
      )}

    </React.Fragment>
  ))}
</div>

          {filtered.length === 0 && (
            <div className="rounded-[24px] border border-[#2A2A2A] bg-white/5 p-14 text-center">
              <div className="text-4xl">🔎</div>
              <h3 className="cmd-serif mt-4 text-2xl font-bold text-[#FFFFFF]">No codes found</h3>
              <p className="mt-2 text-sm text-[#AAAAAA]">Try another keyword, camera move or visual effect.</p>
            </div>
          )}
        </section>

       <section className="mx-auto max-w-[1140px] px-5 pb-14 sm:px-8">
  <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em]">
    ⚡ BEST CODE COMBINATIONS
  </div>

  <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">
    Best Google Flow Code Combinations
  </h2>

  

  <div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
    {(Array.isArray(comboSections[0]?.[1]) ? comboSections[0][1] : []).map(
      ([name, saving, detail], i) => (
        <div
          key={name}
          className="group rounded-[20px] border border-[#2F2F2F] bg-[#0D0D0D] p-6 text-center transition duration-200 hover:border-[#555555] hover:bg-[#111111]"
        >
          {/* Combination number */}
         <div className="flex flex-col items-center">
  <div className="text-[12px] font-bold uppercase tracking-[.16em] text-[#fff]">
    COMBINATION #{i + 1}
  </div>

  <div className="mt-2 h-[2px] w-10 rounded-full bg-[#FF5F87]" />
</div>

          {/* Title */}
          <div className="cmd-serif mt-2 text-[18px] font-bold text-[#FFFFFF]">
            {name}
          </div>

          {/* Code combination */}
          <div className="mt-3 rounded-xl border border-[#292929] bg-[#181818] px-3 py-2.5 text-left">
            <code className="break-words text-[10px] font-semibold leading-5 text-[#FFFFFF]">
              {saving}
            </code>
          </div>
          <p className="mt-3 text-[11px] leading-5 text-[#999999]">{detail}</p>
        </div>
      )
    )}
  </div>
</section>


       <section className="mx-auto max-w-[1140px] px-5 pb-14 sm:px-8">
         <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em]">
           🎬 PROMPT EXAMPLES
         </div>
         <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">Turn Codes Into Better Prompts</h2>
         <div className="mt-8 grid gap-4 md:grid-cols-2">
           {[
             ["360° Character Video", "/orbit360 /slowmo /cinematic", "A man standing confidently in the middle of an empty road while the camera smoothly circles around him, subtle slow-motion clothing movement and cinematic sunset lighting."],
             ["Drone Travel Reveal", "/drone /aerialreveal /wide /goldenhour", "An aerial camera flies above a green mountain valley at sunrise before gradually revealing a person standing on a cliff, soft clouds moving below and realistic golden sunlight illuminating the landscape."],
             ["Rack Focus Portrait", "/rackfocus /closeup /cinematic", "Begin focused on flowers in the foreground and smoothly shift focus toward the person standing in the background, maintaining natural cinematic depth of field."],
             ["Viral Walking Sequence", "/backwardtracking /steadicam /mediumshot /goldenhour /cinematic", "A young man walking confidently through a city street during sunset, medium shot, camera smoothly tracking backward in front of him, natural walking movement, warm golden-hour backlighting, shallow depth of field and realistic cinematic atmosphere."]
           ].map(([name, codes, prompt]) => (
             <div key={name} className="rounded-[20px] border border-[#2A2A2A] bg-[#0D0D0D] p-5">
               <div className="text-[9px] font-bold uppercase tracking-[.16em] text-[#999999]">{name}</div>
               <code className="mt-3 block rounded-xl border border-[#292929] bg-[#181818] px-3 py-2 text-[10px] font-semibold leading-5 text-[#FFFFFF]">{codes}</code>
               <p className="mt-3 text-[12px] leading-5 text-[#AAAAAA]">{prompt}</p>
             </div>
           ))}
         </div>
       </section>
       <section className="mx-auto max-w-[1140px] px-5 pb-20 sm:px-8">

  {/* Section Label */}
  <div className="mb-4 inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] text-white px-4 py-1.5 text-[10px] font-bold uppercase tracking-[.14em]">
    💡 HOW TO USE GOOGLE FLOW CODES
  </div>

  {/* Heading */}
  <h2 className="cmd-serif text-center text-[32px] font-bold text-[#FFFFFF] sm:text-[40px]">
    Use Google Flow Codes Correctly
  </h2>

  {/* Underline */}
  

  {/* Cards */}
  <div className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">

    {(Array.isArray(comboSections[1]?.[1])
      ? comboSections[1][1]
      : []
    ).map(([title, body], i) => (

      <div
        key={title}
        className="group relative rounded-[20px] border border-[#2A2A2A] bg-[#0D0D0D] p-5 transition-all duration-200 hover:-translate-y-1 hover:border-[#444444] hover:bg-[#111111]"
      >

        <div className="flex items-start gap-4">

          {/* Number */}
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#2A2A2A] bg-[#181818] text-[12px] font-bold text-[#FFFFFF] transition group-hover:border-[#FF5F87] group-hover:text-[#FF5F87]">
            {i + 1}
          </div>

          <div className="min-w-0 flex-1">

            {/* Title */}
            <div className="text-[13px] font-bold leading-5 text-[#FFFFFF]">
              {title}
            </div>

         
            {/* Description */}
            <p className="mt-2 text-[12px] leading-5 text-[#999999]">
              {body}
            </p>

          </div>

        </div>

      </div>

    ))}

  </div>
</section>

        {showUnlockModal && !isUnlocked && (
          <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 px-4 py-6 backdrop-blur-sm"
            onClick={() => setShowUnlockModal(false)}
          >
            <div
              className="relative max-h-[90vh] w-full max-w-[520px] overflow-y-auto rounded-[24px] border border-[#333333] bg-[#181818] p-5 shadow-[0_20px_70px_rgba(0,0,0,.55)] sm:p-7"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setShowUnlockModal(false)}
                className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full border border-[#333333] bg-[#222222] text-[#AAAAAA] transition hover:text-white"
                aria-label="Close"
              >
                ×
              </button>

              <div className="pr-8">
                <div className="inline-flex rounded-full bg-[linear-gradient(45deg,#d54d09,#e1307a,#9443ff,#436cff)] px-3 py-1 text-[9px] font-bold uppercase tracking-[.14em] text-white">
                  🔒 Unlock Full Library
                </div>

                <h3 className="cmd-serif mt-4 text-[28px] font-bold leading-tight text-white">
                  Unlock All 205+ Secret Codes
                </h3>

                <p className="mt-3 text-[13px] leading-5 text-[#AAAAAA]">
                  The first 30 secret codes are free. Enter your details to
                  unlock the complete Flow AI Secret Code Library.
                </p>
              </div>

              <form onSubmit={handleLeadSubmit} className="mt-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-[#CCCCCC]">
                    Name
                  </label>
                  <input
                    type="text"
                    value={leadForm.name}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                    placeholder="Enter your name"
                    className="h-12 w-full rounded-xl border border-[#333333] bg-[#0D0D0D] px-4 text-sm text-white outline-none transition placeholder:text-[#666666] focus:border-[#FF5F87]"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-[#CCCCCC]">
                    Email
                  </label>
                  <input
                    type="email"
                    value={leadForm.email}
                    onChange={(e) =>
                      setLeadForm((prev) => ({ ...prev, email: e.target.value }))
                    }
                    placeholder="Enter your email"
                    className="h-12 w-full rounded-xl border border-[#333333] bg-[#0D0D0D] px-4 text-sm text-white outline-none transition placeholder:text-[#666666] focus:border-[#FF5F87]"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-[#CCCCCC]">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    value={leadForm.mobile}
                    onChange={(e) =>
                      setLeadForm((prev) => ({
                        ...prev,
                        mobile: e.target.value.replace(/\\D/g, "").slice(0, 10),
                      }))
                    }
                    placeholder="Enter 10-digit mobile number"
                    className="h-12 w-full rounded-xl border border-[#333333] bg-[#0D0D0D] px-4 text-sm text-white outline-none transition placeholder:text-[#666666] focus:border-[#FF5F87]"
                  />
                </div>

                {leadError && (
                  <div className="rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-[11px] leading-4 text-red-300">
                    {leadError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSavingMobile}
                  className="flex h-12 w-full items-center justify-center rounded-xl bg-white px-5 text-xs font-bold uppercase tracking-[.08em] text-black transition hover:bg-[#FF5F87] hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingMobile ? "Unlocking..." : "Unlock All Secret Codes →"}
                </button>

                <p className="text-center text-[10px] leading-4 text-[#777777]">
                  Your email will be used for future AI updates &amp; resources.
                </p>
              </form>
            </div>
          </div>
        )}

        <footer className="border-t border-[#2A2A2A] bg-[#080808] py-10 text-center">
          <div className="cmd-serif text-[22px] font-bold text-[#FFFFFF]">200+ Google Flow Secret Codes 2026</div>
          <p className="mx-auto mt-2 max-w-[700px] px-5 text-xs leading-5 text-[#AAAAAA]">Use these as reusable camera and video prompt cues. Results can vary by model, scene, wording and generation settings.</p>
          <a href={LEARN_URL} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[.12em] text-[#FFFFFF] transition hover:text-[#FFFFFF] hover:underline">Learn more about practical AI workflows <FiArrowUpRight/></a>
        </footer>
      </main>
    </div>
  );
}
